/**
 * Single source of truth for privilege names on the frontend.
 *
 * Values are the exact strings the backend stores in `privilege.name` — they
 * cannot change without a coordinated DB + backend migration. The KEYS give
 * type-safe, IDE-renameable identifiers for use throughout the frontend.
 *
 * Usage:
 *   import { Privileges, PrivilegeName } from 'src/models/privileges';
 *   user.hasPrivilege(Privileges.RehearsalEdit);     // in TS / component code
 *   data: { privilege: Privileges.UsersAdmin }       // in route config
 *
 * Template usage: expose the const as a component property
 *   protected readonly privileges = Privileges;
 * then in the template: `*ngIf="hasPrivilege(privileges.RehearsalEdit)"`.
 *
 * When the backend adds a new privilege, add the key+value here and it
 * becomes available everywhere with autocomplete + compile-time checks.
 */
export const Privileges = {
    AvailabilitiesView: 'availabilities_view',
    ContactAdmin: 'contact_admin',
    EmailTestAdmin: 'email_test_admin',
    MagicalunaInspectionsAdmin: 'magicaluna_inspections_admin',
    MagicalunaInspectionsCreate: 'magicaluna_inspections_create',
    MagicalunaInspectionsView: 'magicaluna_inspections_view',
    MaintenanceTaskEdit: 'maintenancetask_edit',
    MaintenanceTaskCategoryEdit: 'maintenancetaskcategory_edit',
    MaintenanceTaskCategoryDelete: 'maintenancetaskcategory_delete',
    /** Closed-beta access to the personal notification-preferences section on /settings. */
    NotificationsBeta: 'notifications_beta',
    PerformanceNoteEdit: 'performance_note_edit',
    PerformancesEdit: 'performances_edit',
    PlannerEdit: 'planner_edit',
    PlannerView: 'planner_view',
    PresetEdit: 'preset_edit',
    PrivilegesAdmin: 'privileges_admin',
    RehearsalEdit: 'rehearsal_edit',
    SceneAdmin: 'scene_admin',
    SettingsView: 'settings_view',
    ShowreportCreate: 'showreport_create',
    ShowreportView: 'showreport_view',
    ShowsAdmin: 'shows_admin',
    ShowsEditFeatureFlags: 'shows_edit_feature_flags',
    ShowsUpdate: 'shows_update',
    TemplateApprove: 'template_approve',
    TemplateEdit: 'template_edit',
    TemplateView: 'template_view',
    UsersAdmin: 'users_admin',
    UsersCreate: 'users_create',
    UsersEdit: 'users_edit',
    UsersUpdate: 'users_update'
} as const;

/**
 * Union type of all valid privilege name strings. Use this anywhere you
 * currently have `string` for a privilege name to get compile-time safety
 * that only real privileges are passed in.
 */
export type PrivilegeName = typeof Privileges[keyof typeof Privileges];
